/**
 * PATH: packages/document-editor/src/extensions.ts
 * FILE_ID: DOCEDITOR-EXTENSIONS-001
 * PURPOSE: Generic TipTap schema assembly with explicit domain extension slots.
 * WHY: Product packages extend one editor core instead of forking its schema.
 * RELATED: reportExtensions.ts, imageExtension.ts
 */
import type { AnyExtension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { TextStyleKit } from '@tiptap/extension-text-style'
import { Highlight } from '@tiptap/extension-highlight'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextAlign } from '@tiptap/extension-text-align'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import { DocumentImage } from './imageExtension'
import { REPORT_EXTENSIONS } from './reportExtensions'

export interface DocumentExtensionOptions {
  placeholder?: string
  domainExtensions?: AnyExtension[]
  includeTables?: boolean
  includeImages?: boolean
  includeReportNodes?: boolean
}

export function buildDocumentExtensions(
  options: DocumentExtensionOptions = {},
): AnyExtension[] {
  const extensions: AnyExtension[] = [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] },
      link: {
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      },
      dropcursor: { color: 'rgba(99,102,241,0.5)', width: 2 },
    }),
    TextStyleKit,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Placeholder.configure({
      placeholder: options.placeholder ?? 'Start writing…',
      showOnlyWhenEditable: true,
      includeChildren: false,
    }),
  ]
  if (options.includeImages !== false) extensions.push(DocumentImage)
  if (options.includeTables !== false) {
    extensions.push(
      Table.configure({ resizable: true, allowTableNodeSelection: true }),
      TableRow,
      TableHeader,
      TableCell,
    )
  }
  if (options.includeReportNodes !== false) extensions.push(...REPORT_EXTENSIONS)
  extensions.push(...(options.domainExtensions ?? []))
  return extensions
}

export const DOCUMENT_EDITOR_CSS = `
.qdoc-editor { --qdoc-page-shadow:0 3px 12px rgba(15,23,42,.16); color:#0f172a; }
.qdoc-editor .qdoc-page-shell button,.qdoc-editor .qdoc-page-shell select,.qdoc-editor .qdoc-page-shell input { border:1px solid #cbd5e1; border-radius:4px; min-height:30px; padding:3px 7px; background:#fff; }
.qdoc-editor .qdoc-page-shell button:focus-visible,.qdoc-editor .qdoc-page-shell select:focus-visible,.qdoc-editor .qdoc-page-shell input:focus-visible,.qdoc-editor .qdoc-page-shell [tabindex]:focus-visible { outline:2px solid #2563eb; outline-offset:2px; }
.qdoc-editor .qdoc-page-shell button:disabled { cursor:not-allowed; opacity:.45; }
.qdoc-editor .qdoc-control,.qdoc-editor .qdoc-context-controls { display:inline-flex; align-items:center; gap:6px; }
.qdoc-editor .qdoc-page-viewport { max-height:min(75vh,900px); overscroll-behavior:contain; }
.qdoc-editor .qdoc-a4-page { box-sizing:border-box; background:repeating-linear-gradient(to bottom,#fff 0,#fff var(--qdoc-page-height),#e2e8f0 var(--qdoc-page-height),#e2e8f0 calc(var(--qdoc-page-height) + var(--qdoc-page-gap))); box-shadow:var(--qdoc-page-shadow); }
.qdoc-editor .ProseMirror { box-sizing:border-box; outline:none; min-height:240px; }
.qdoc-editor .ProseMirror > [data-qdoc-page-spacer] { margin-top:var(--qdoc-pagination-spacer)!important; }
.qdoc-editor .ProseMirror h1 { font-size:1.6em; font-weight:700; line-height:1.25; margin:.5em 0 .35em; }
.qdoc-editor .ProseMirror h2 { font-size:1.3em; font-weight:700; line-height:1.3; margin:.5em 0 .3em; }
.qdoc-editor .ProseMirror h3 { font-size:1.1em; font-weight:600; line-height:1.35; margin:.45em 0 .25em; }
.qdoc-editor .ProseMirror h4 { font-size:1.1em; font-weight:600; margin:.45em 0 .2em; }
.qdoc-editor .ProseMirror ul { list-style:disc outside; margin:.5em 0; padding-left:1.6em; }
.qdoc-editor .ProseMirror ol { list-style:decimal outside; margin:.5em 0; padding-left:1.6em; }
.qdoc-editor .ProseMirror li { margin:.15em 0; }
.qdoc-editor .ProseMirror li > p { margin:0; }
.qdoc-editor .ProseMirror blockquote { border-left:3px solid #cbd5e1; margin:.5em 0; padding-left:.9em; color:#475569; font-style:italic; }
.qdoc-editor .ProseMirror a { color:#2563eb; text-decoration:underline; }
.qdoc-editor .ProseMirror hr { border:0; border-top:1px solid #cbd5e1; margin:.8em 0; }
.qdoc-editor .ProseMirror img { display:block; max-width:100%; height:auto; }
.qdoc-editor .ProseMirror img[data-align="left"] { margin-left:0; margin-right:auto; }
.qdoc-editor .ProseMirror img[data-align="center"] { margin-left:auto; margin-right:auto; }
.qdoc-editor .ProseMirror img[data-align="right"] { margin-left:auto; margin-right:0; }
.qdoc-editor .ProseMirror img.ProseMirror-selectednode { outline:3px solid #2563eb; outline-offset:2px; }
.qdoc-editor .ProseMirror table { border-collapse:collapse; margin:8px 0; width:100%; table-layout:fixed; overflow:hidden; }
.qdoc-editor .ProseMirror th,.qdoc-editor .ProseMirror td { border:1px solid #d1d5db; padding:6px 8px; vertical-align:top; position:relative; min-width:2em; }
.qdoc-editor .ProseMirror th { background:#f3f4f6; font-weight:600; text-align:left; }
.qdoc-editor .ProseMirror .selectedCell:after { content:""; position:absolute; inset:0; background:rgba(37,99,235,.1); pointer-events:none; }
.qdoc-editor .ProseMirror .column-resize-handle { position:absolute; right:-2px; top:0; bottom:0; width:4px; background:#2563eb; cursor:col-resize; }
.qdoc-editor .qdoc-page-break { border:0; border-top:2px dashed #64748b; min-height:1px; }
.qdoc-editor .qdoc-page-break::after { content:"Page break"; display:block; color:#475569; font-size:11px; text-align:center; }
.qdoc-editor [data-qdoc-node="section-break"]::after { content:"Section break"; color:#64748b; font-size:11px; }
.qdoc-editor [data-qdoc-node="generated-fact"],.qdoc-editor [data-qdoc-node="audited-override"] { position:relative; margin:.75rem 0; border-radius:4px; padding:28px 12px 10px; }
.qdoc-editor [data-qdoc-node="generated-fact"] { border:1px solid #7dd3fc; border-left:4px solid #0284c7; background:#f0f9ff; }
.qdoc-editor [data-qdoc-node="generated-fact"]::before { content:"Source fact · " attr(data-source) " · " attr(data-status); position:absolute; left:10px; top:7px; color:#075985; font-size:11px; font-weight:700; letter-spacing:.02em; }
.qdoc-editor [data-qdoc-node="audited-override"] { border:1px solid #fcd34d; border-left:4px solid #d97706; background:#fffbeb; }
.qdoc-editor [data-qdoc-node="audited-override"]::before { content:"Audited override · Reason: " attr(data-reason); position:absolute; left:10px; top:7px; color:#92400e; font-size:11px; font-weight:700; }
.qdoc-editor [data-qdoc-node="merge-field"] { background:#e0e7ff; color:#3730a3; border-radius:3px; padding:1px 3px; }
.qdoc-editor .ProseMirror p.is-editor-empty:first-child::before { content:attr(data-placeholder); color:#94a3b8; float:left; height:0; pointer-events:none; }
.qdoc-editor .qdoc-page-settings { position:relative; }
.qdoc-editor .qdoc-page-settings summary { cursor:pointer; }
.qdoc-editor .qdoc-page-settings-panel { position:absolute; z-index:20; top:calc(100% + 8px); left:0; display:grid; gap:8px; width:230px; padding:12px; border:1px solid #cbd5e1; border-radius:6px; background:#fff; box-shadow:0 8px 24px rgba(15,23,42,.18); }
.qdoc-editor .qdoc-page-settings-panel label { display:grid; gap:3px; }
@media (max-width:720px) {
  .qdoc-editor .qdoc-page-viewport { max-height:65vh; padding:12px; }
  .qdoc-editor .qdoc-page-shell > header { position:sticky; top:0; z-index:30; }
  .qdoc-editor .qdoc-toolbar { max-height:96px; overflow:auto; }
  .qdoc-editor .qdoc-scaled-paper { margin-left:0; }
}
@media print {
  .qdoc-editor .qdoc-page-shell { border:0!important; overflow:visible!important; background:#fff!important; }
  .qdoc-editor .qdoc-page-shell > header,.qdoc-editor .qdoc-toolbar { display:none!important; }
  .qdoc-editor .qdoc-page-viewport { max-height:none!important; overflow:visible!important; padding:0!important; }
  .qdoc-editor .qdoc-scaled-paper { width:auto!important; height:auto!important; }
  .qdoc-editor .qdoc-a4-page { width:auto!important; min-height:0!important; padding:0!important; transform:none!important; background:#fff!important; box-shadow:none!important; }
  .qdoc-editor .ProseMirror > [data-qdoc-page-spacer] { margin-top:0!important; }
  .qdoc-editor [data-qdoc-node="page-break"],.qdoc-editor [data-qdoc-node="section-break"][data-kind="nextPage"] { break-after:page; height:0!important; border:0!important; }
  .qdoc-editor .qdoc-page-break::after,.qdoc-editor [data-qdoc-node="section-break"]::after { display:none!important; }
  .qdoc-editor .ProseMirror table,.qdoc-editor .ProseMirror img,.qdoc-editor [data-qdoc-node="generated-fact"],.qdoc-editor [data-qdoc-node="audited-override"] { break-inside:avoid; }
}
`
